import { FC, type MouseEvent } from 'react';
import { MenuIcon } from './MenuIcon';
import { useSidebar } from './SidebarContext';
import { useCurrentPath } from './useCurrentPath';
import { GetPermissions, isSuperAdmin } from '../../../../../app/modules/auth';

type CollapsedItemProps = {
  icon: string;
  title: string;
  to?: string;
  isActive?: boolean;
  hasSubmenu?: boolean;
};

const CollapsedItem: FC<CollapsedItemProps> = ({ icon, title, to, isActive = false, hasSubmenu = false }) => {
  const { openMenuOnExpand } = useSidebar();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (hasSubmenu) {
      openMenuOnExpand(title);
      return;
    }

    if (to) {
      window.history.pushState({}, '', to);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  return (
    <a
      href={to || '#'}
      onClick={handleClick}
      className={`
        group flex items-center justify-center w-10 h-10 rounded-lg mx-auto
        transition-all duration-200 cursor-pointer
        ${isActive
          ? 'bg-emerald-500/20 text-emerald-400'
          : 'bg-white/[0.07] text-slate-300 hover:bg-white/[0.14] hover:text-white'
        }
      `}
      title={title}
      aria-label={hasSubmenu ? `Abrir submenu ${title}` : title}
      aria-haspopup={hasSubmenu ? 'menu' : undefined}
    >
      <MenuIcon icon={icon} size={20} />
    </a>
  );
};

const menuItems = [
  { icon: '/media/icons/duotune/menu/home.svg', title: 'Pagina Inicial', to: '/home', activePaths: ['/home'] },
  { icon: '/media/icons/duotune/menu/unilever-white.svg', title: 'Unilever', to: '/cashback', hasSubmenu: true, activePaths: ['/ul-'] },
  { icon: '/media/icons/duotune/menu/cashback.svg', title: 'Cashback', to: '/cashback', hasSubmenu: true, activePaths: ['/cashback', '/lojas', '/listas', '/regionais', '/tamanhos', '/bus', '/precos', '/cupons', '/relatorios', '/alerts', '/validacoes'] },
  { icon: '/media/icons/duotune/menu/conecta/conecta.svg', title: 'Conecta', hasSubmenu: true, activePaths: ['/conecta'] },
  { icon: '/media/icons/duotune/menu/datatransfer/data-transfer.svg', title: 'Data Transfer', hasSubmenu: true, activePaths: ['/datatransfer'] },
  { icon: '/media/icons/duotune/menu/reports/reports.svg', title: 'Relatórios', hasSubmenu: true, activePaths: ['/reports'] },
  { icon: '/media/icons/duotune/menu/api-manage-users.svg', title: 'API', hasSubmenu: true, activePaths: ['/api'] },
  { icon: '/media/icons/duotune/menu/robot-ai-svgrepo-com.svg', title: 'Compra AI', to: '/ai', activePaths: ['/ai'] },
  { icon: '/media/icons/duotune/menu/campaign.svg', title: 'Simulador de Campanhas', to: '/campaigns', activePaths: ['/campaigns'] },
  { icon: '/media/icons/duotune/menu/permissions.svg', title: 'Seller Mapping', to: '/seller-mapping', activePaths: ['/seller-mapping'] },
  { icon: '/media/icons/duotune/menu/infrapay.svg', title: 'Infrapay', hasSubmenu: true, activePaths: ['/infrapay'] },
  { icon: '/media/icons/duotune/menu/devops-servers.svg', title: 'DevOps', hasSubmenu: true, activePaths: ['/devops'] },
  { icon: '/media/icons/duotune/menu/briefing-promotion.svg', title: 'Briefing Promoções', hasSubmenu: true, activePaths: ['/briefing-promocoes'] },
  { icon: '/media/icons/duotune/menu/permissions2.svg', title: 'Administrativo', hasSubmenu: true, activePaths: ['/analytics', '/fechamentos', '/logs', '/development-features', '/permissions', '/settings', '/users', '/nps'], requiresAdministrativeAccess: true },
];

const CollapsedMenu: FC = () => {
  const currentPath = useCurrentPath();
  const permissions = GetPermissions();
  const canViewAdministrative = !!permissions?.length && (
    isSuperAdmin() || permissions.some((feature) =>
      ['analytics-view', 'logs-view', 'development-features-manage', 'fechamentos-view'].includes(feature.name) ||
      feature.name.includes('settings-') ||
      feature.name.includes('users-view') ||
      feature.name.includes('nps-view')
    )
  );

  return (
    <div className="flex flex-col gap-2 w-full px-2">
      {menuItems
        .filter((item) => !('requiresAdministrativeAccess' in item) || canViewAdministrative)
        .map((item) => (
        <CollapsedItem
          key={item.title}
          icon={item.icon}
          title={item.title}
          to={item.to}
          isActive={item.activePaths.some(path => currentPath === path || currentPath.startsWith(`${path}/`))}
          hasSubmenu={item.hasSubmenu}
        />
      ))}
    </div>
  );
};

export { CollapsedMenu };
